// btw-front/src/components/system/ScrollToTop.jsx
// 화면 전환 시 스크롤 위치 관리.
// - 새 화면으로 이동(PUSH/REPLACE 로 경로가 바뀜): 맨 위로
// - 뒤로가기·앞으로가기(POP, '목록으로' 버튼의 navigate(-1) 포함): 그 화면을 떠날 때의 위치로 복원
// 같은 경로에서 쿼리만 바뀌는 경우(탭·필터 변경)는 스크롤을 건드리지 않는다.
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "btw-scroll-positions";
// 목록 데이터가 비동기로 그려지는 동안 복원을 재시도하는 최대 시간
const RESTORE_TIMEOUT_MS = 3000;
// 사용자가 직접 스크롤을 시작하면 복원을 중단한다
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];
// react-router 가 history.state 에 키가 없는 첫 항목(주소 직접 입력·북마크·외부 링크로 새로 띄운 화면)에 주는 고정 키.
// 같은 탭에서 새로 띄운 화면끼리 이 키를 공유하므로 그대로 쓰면 다른 화면의 위치가 복원된다
// → resolveKey 가 그 항목에 고유 키를 붙여 준다. 이 키 자체로는 저장·복원하지 않는다.
const SHARED_INITIAL_KEY = "default";
// 보관할 최대 항목 수 — 검색어 입력마다 쿼리 갱신으로 새 키가 생기므로 오래된 것부터 버린다
const MAX_POSITIONS = 100;

// 히스토리 항목(location.key)별 스크롤 위치. 새로고침 후 뒤로가기도 복원되도록 sessionStorage 에 둔다.
// 저장소를 쓸 수 없는 환경(사파리 비공개 모드 등)에서는 메모리만 사용.
// Map 은 넣은 순서를 지키므로 맨 앞이 가장 오래된 항목이다(일반 객체는 숫자 모양 키를 앞으로 재정렬해 순서가 깨진다).
const positions = (() => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return new Map(Array.isArray(saved) ? saved : []);
  } catch (e) {
    return new Map();
  }
})();

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...positions]));
  } catch (e) {
    // 저장 실패는 무시 — 메모리 값으로 동작
  }
}

// 위치를 기록할 키. 새로 띄운 첫 항목(고정 키)이면 고유 키를 만들어 history.state 에 직접 적는다.
// react-router 는 뒤로·앞으로·새로고침 때 history.state.key 를 location.key 로 읽으므로, 다음에 이 항목으로
// 돌아오면 이 키로 복원된다. 이동(navigate)을 일으키지 않아 로그인 가드 리다이렉트 등과 부딪치지 않는다.
function resolveKey(locationKey) {
  if (locationKey !== SHARED_INITIAL_KEY) return locationKey;
  try {
    const state = window.history.state || {};
    if (state.key) return state.key;
    const key = Math.random().toString(36).slice(2, 10);
    window.history.replaceState({ ...state, key }, "");
    return key;
  } catch (e) {
    return SHARED_INITIAL_KEY;
  }
}

function savePosition(key, y) {
  if (key === SHARED_INITIAL_KEY) return;
  // 지웠다 다시 넣어 가장 최근 항목으로 옮긴다
  positions.delete(key);
  positions.set(key, y);
  while (positions.size > MAX_POSITIONS) positions.delete(positions.keys().next().value);
  persist();
}

function savedPosition(key) {
  return key === SHARED_INITIAL_KEY ? undefined : positions.get(key);
}

export default function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathname = useRef(location.pathname);
  // 복원 중에는 스크롤 이벤트로 위치를 저장하지 않는다(덜 그려진 화면의 중간값이 덮어쓰는 것 방지)
  const restoring = useRef(false);
  const currentKey = useRef(SHARED_INITIAL_KEY);

  // 브라우저 자체 복원 끄기 — 데이터가 그려지기 전에 복원을 시도해 맨 위로 떨어지므로 직접 처리
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // 현재 히스토리 항목의 스크롤 위치 기록. 키는 ref 로 읽는다 — 화면 전환 직후의 맨 위 이동이
  // 이전 화면 키로 저장되면 복원 위치가 0 으로 덮이므로, 리스너는 1개만 두고 키만 갈아 끼운다.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (restoring.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        savePosition(currentKey.current, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  useLayoutEffect(() => {
    const key = resolveKey(location.key);
    currentKey.current = key;
    const pathChanged = prevPathname.current !== location.pathname;
    prevPathname.current = location.pathname;

    if (navigationType !== "POP") {
      if (pathChanged) window.scrollTo(0, 0);
      // 같은 화면에서 쿼리만 바뀐 경우(필터 변경 등 replace)는 키만 새로 생기므로 현재 위치를 이어 받는다.
      // 이후 스크롤 없이 상세로 갔다 돌아와도 복원되도록.
      else savePosition(key, window.scrollY);
      return undefined;
    }

    const target = savedPosition(key);
    if (!target) {
      if (pathChanged) window.scrollTo(0, 0);
      return undefined;
    }

    // 목록이 다 그려져 목표 위치까지 스크롤할 수 있을 때까지 매 프레임 재시도
    restoring.current = true;
    const startedAt = Date.now();
    let frame = 0;
    const stop = () => {
      restoring.current = false;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      USER_SCROLL_EVENTS.forEach((ev) => window.removeEventListener(ev, stop));
    };
    const attempt = () => {
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) < 2;
      if (reached || Date.now() - startedAt > RESTORE_TIMEOUT_MS) stop();
      else frame = requestAnimationFrame(attempt);
    };
    USER_SCROLL_EVENTS.forEach((ev) => window.addEventListener(ev, stop, { passive: true }));
    attempt();
    return stop;
  }, [location.key, location.pathname, navigationType]);

  return null;
}
